import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import { getAmChartsLocale } from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import { shortCompanyName } from "../../../utils/shortenName";

const ExpeditorActivityChart = ({ item }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();

  const colors = {
    success: "#4E7CFF",
    failure: "#FFB5CE",
  };

  const processExpeditorData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    return data.map((expeditor) => {
      const total = expeditor.success + expeditor.failure;
      const successPercent = total > 0 ? Math.round((expeditor.success / total) * 100) : 0;
      const failurePercent = total > 0 ? Math.round((expeditor.failure / total) * 100) : 0;

      return {
        expeditorName: expeditor.expeditorName,
        shortExpeditorName: shortCompanyName(expeditor.expeditorName), 
        success: expeditor.success,
        failure: expeditor.failure,
        successPercent,
        failurePercent,
        total,
      };
    });
  };

  const processedData = useMemo(() => {
    return processExpeditorData(item);
  }, [item]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-expeditor-activity");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomY",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    let yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "expeditorName",
        renderer: am5xy.AxisRendererY.new(root, {
          inversed: true,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
          minGridDistance: 30,
        }),
      }),
    );

    yAxis.data.setAll(processedData);

    yAxis.get("renderer").labels.template.adapters.add("text", (text, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        return dataItem.dataContext.shortExpeditorName;
      }
      return text;
    });

    let xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {
          strokeOpacity: 0.1,
          minGridDistance: 50,
        }),
        min: 0,
        max: 100,
      }),
    );

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomY",
        yAxis: yAxis,
      }),
    );
    // cursor.lineX.set("forceHidden", true);
    // cursor.lineY.set("forceHidden", true);

    yAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    xAxis.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    xAxis.setAll({
      min: 0,
      max: 100,
      strictMinMax: true,
      numberFormat: "#'%'",
    });

    function createSeries(field, name, color, percentField, countField) {
      var series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: name,
          xAxis: xAxis,
          yAxis: yAxis,
          valueXField: percentField,
          categoryYField: "expeditorName",
          sequencedInterpolation: true,
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: "horizontal",
            labelText: "{name}[/]\n{expeditorName}: {" + percentField + "}% ({" + countField + "})",
          }),
        }),
      );

      series.columns.template.setAll({
        height: am5.p100,
        strokeOpacity: 0,
        fill: am5.color(color),
        maxHeight: 80
      });

      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          locationX: 1,
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            centerY: am5.p50,
            text: "{" + percentField + "}%",
            populateText: true,
            fill: am5.color("#333333"),
            fontSize: 11,
          }),
        });
      });

      series.data.setAll(processedData);
      series.appear();

      return series;
    }

    createSeries("success", t("analytics.successful"), colors.success, "successPercent", "success");
    createSeries("failure", t("analytics.unsuccessful"), colors.failure, "failurePercent", "failure");

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, currentLanguage]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.participationAndWinRate")}</span>
      </div>

      <div className={cn(style.area__namesMobile)}>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: colors.success }}></div>
          <div className={style.area_type}>{t("analytics.winRate")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: colors.failure }}></div>
          <div className={style.area_type}>{t("analytics.lossRate")}</div>
        </div>
      </div>

      <div className={style.area}>
        <div id="chartdiv-expeditor-activity" className={style.area_chart}></div>
        <div className={style.area_names}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: colors.success }}></div>
            <div className={style.area_type}>{t("analytics.winRate")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: colors.failure }}></div>
            <div className={style.area_type}>{t("analytics.lossRate")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpeditorActivityChart;
